import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  LinearProgress,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  useTheme,
  Slider,
} from "@mui/material";
import {
  SmartToy,
  AssessmentOutlined,
  VerifiedUserOutlined,
  History,
  InfoOutlined,
  CheckCircleOutline,
  WarningAmber,
  ErrorOutline,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

interface DetectionDetails {
  perplexity_score: number;
  burstiness_score: number;
  lexical_diversity: number;
  transition_patterns: number;
  sentence_structure: number;
  repetition_score: number;
  word_count: number;
  sentence_count: number;
}

interface SentenceAnalysis {
  text: string;
  perplexity: number;
  ai_score: number;
  is_ai: boolean;
  indicators?: string[];
}

interface DetectionResult {
  ai_probability: number;
  verdict: string;
  confidence: string;
  details: DetectionDetails;
  interpretation: {
    perplexity: string;
    burstiness: string;
    diversity: string;
  };
  sentence_analysis: SentenceAnalysis[];
  scan_id?: string;
}

export default function AIDetector(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [viewMode, setViewMode] = useState<"edit" | "highlight">("edit");
  const [selectedSentenceIndex, setSelectedSentenceIndex] = useState<
    number | null
  >(null);
  const [recentScans, setRecentScans] = useState<
    { label: string; score: number; date: Date }[]
  >([]);
  const [feedbackStatus, setFeedbackStatus] = useState<
    "none" | "loading" | "success"
  >("none");
  const [pendingLabel, setPendingLabel] = useState<"Human" | "AI" | null>(null);
  const [actualProb, setActualProb] = useState<number>(100);
  const [affectedSections, setAffectedSections] = useState<string[]>([]);

  // Thresholds - Adjusted for better visual alignment with global score
  const THRESHOLD_AI = 60;
  const THRESHOLD_REFINED = 30;

  const handleScan = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    setResult(null);
    setFeedbackStatus("none");
    setPendingLabel(null);
    setActualProb(100); // Default to 100 for AI reports
    setAffectedSections([]);
    setSelectedSentenceIndex(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/detect-ai`, {
        text: inputText,
        language: language,
      }, { timeout: 90000 });

      if (response.data) {
        setResult(response.data);
        setViewMode("highlight");

        // Add to recent scans
        const label =
          inputText.split(/\s+/).slice(0, 4).join(" ") +
          (inputText.split(/\s+/).length > 4 ? "..." : "");
        setRecentScans((prev) =>
          [
            {
              label: label || "Untitled Scan",
              score: response.data.ai_probability,
              date: new Date(),
            },
            ...prev,
          ].slice(0, 5),
        );
      }
    } catch (error) {
      console.error("Detection failed:", error);
      alert("AI detection failed. Please make sure the backend is running.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setViewMode("edit");
    setResult(null);
    setSelectedSentenceIndex(null);
  };

  const handleReportError = async () => {
    if (!result?.scan_id || !pendingLabel) return;
    setFeedbackStatus("loading");
    try {
      await axios.post(`${API_BASE_URL}/report-error`, {
        scan_id: result.scan_id,
        actual_label: pendingLabel,
        actual_probability: actualProb,
        affected_sections: affectedSections,
      });
      setFeedbackStatus("success");
    } catch (err) {
      console.error("Feedback error:", err);
      setFeedbackStatus("none");
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (!pastedText) return;

    // Smart Clean Logic for PDF/Academic copies:
    const cleaned = pastedText
      .replace(/(\w+)-\s*[\r\n]+\s*(\w+)/g, "$1$2") // Join hyphenated words
      .replace(/([^\n])\r?\n([^\n])/g, "$1 $2") // Join single line breaks
      .replace(/\n{3,}/g, "\n\n") // Normalize paragraph breaks
      .replace(/[ ]+/g, " ") // Collapse extra spaces
      .trim();

    // Insert at selection position
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const newText =
      inputText.substring(0, start) + cleaned + inputText.substring(end);

    setInputText(newText);
  };

  const getBreakdown = () => {
    if (!result?.sentence_analysis) return { ai: 0, refined: 0, human: 100 };
    const total = result.sentence_analysis.length;
    if (total === 0) return { ai: 0, refined: 0, human: 100 };

    const aiCount = result.sentence_analysis.filter(
      (s) => s.ai_score >= THRESHOLD_AI,
    ).length;
    const refinedCount = result.sentence_analysis.filter(
      (s) => s.ai_score >= THRESHOLD_REFINED && s.ai_score < THRESHOLD_AI,
    ).length;

    // Use global score to drive the base of the breakdown for AI / Human
    const globalAI = Math.round(result.ai_probability);

    // We want the AI bar to strictly match the global AI probability to avoid confusion.
    let finalAI = globalAI;

    // The remaining percentage is 100 - finalAI
    let remaining = 100 - finalAI;

    // Assign refined based on its proportion, but cap it at the remaining
    let finalRefined = 0;

    if (refinedCount > 0) {
      // if global AI is low, maybe some refined sentences exist,
      // but we don't want refined to completely overpower the actual human score.
      const actualRefinedPerc = Math.round((refinedCount / total) * 100);

      // Just cap refined to whatever is left from 100 after AI takes its exact share.
      finalRefined = Math.min(actualRefinedPerc, remaining);
    }

    const finalHuman = remaining - finalRefined;

    return {
      ai: finalAI,
      refined: finalRefined,
      human: finalHuman,
    };
  };

  const getScoreColor = (score: number) => {
    if (score < THRESHOLD_REFINED) return "#10b981"; // Green (Human)
    if (score < THRESHOLD_AI) return "#3b82f6"; // Blue (Refined) - following user pref
    return "#f97316"; // Orange (AI)
  };

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case "high":
        return (
          <CheckCircleOutline fontSize="small" sx={{ color: "#10b981" }} />
        );
      case "medium":
        return <WarningAmber fontSize="small" sx={{ color: "#f59e0b" }} />;
      case "low":
        return <ErrorOutline fontSize="small" sx={{ color: "#ef4444" }} />;
      default:
        return undefined;
    }
  };

  const breakdown = getBreakdown();

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box
        sx={{
          mb: 6,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mb: 1,
              letterSpacing: "-0.02em",
              color: isDarkMode ? "white" : "text.primary",
            }}
          >
            AI Detection{" "}
            <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
              Scanner
            </span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? "#9ca3af" : "text.secondary",
              fontWeight: 400,
              maxWidth: 600,
            }}
          >
            Advanced multi-layer AI detection using perplexity, burstiness, and
            lexical diversity analysis.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mb: 1.5,
            }}
          >
            {viewMode === "highlight" && (
              <Button
                size="small"
                onClick={handleEdit}
                sx={{
                  color: isDarkMode ? "#9ca3af" : "primary.main",
                  textTransform: "none",
                  "&:hover": {
                    color: isDarkMode ? "white" : "primary.dark",
                  },
                }}
              >
                Back to Edit
              </Button>
            )}
            <FormControl variant="outlined" size="small">
              <Select
                value={language}
                onChange={(e: SelectChangeEvent) => setLanguage(e.target.value)}
                displayEmpty
                sx={{
                  color: isDarkMode ? "#e5e7eb" : "text.primary",
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
                  borderRadius: "8px",
                  ".MuiSelect-icon": {
                    color: isDarkMode ? "#9ca3af" : "text.secondary",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.2)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "white" : "primary.main",
                  },
                  height: 36,
                  fontSize: "14px",
                }}
              >
                <MenuItem value="en">English (US/UK)</MenuItem>
                <MenuItem value="de">German (Deutsch)</MenuItem>
                <MenuItem value="pl">Polish (Polski)</MenuItem>
                <MenuItem value="tr">Turkish (Türkçe)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              p: { xs: 2.5, md: 3 },
              minHeight: { xs: 350, md: 500 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {viewMode === "edit" ? (
                <TextField
                  fullWidth
                  multiline
                  placeholder="Paste the text you want to scan for AI content..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onPaste={handlePaste}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: { xs: "15px", md: "16px" },
                      lineHeight: 1.8,
                      letterSpacing: "0.01em",
                      color: isDarkMode ? "#e5e7eb" : "#1f2937",
                      alignItems: "flex-start",
                      padding: "8px 0",
                      "& textarea": {
                        height: "100% !important",
                        minHeight: { xs: "180px", md: "420px" },
                      },
                      "& textarea::selection": {
                        backgroundColor: "rgba(99, 102, 241, 0.22)",
                        color: "inherit",
                      },
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    minHeight: 450,
                    color: isDarkMode ? "#d1d5db" : "text.primary",
                    fontSize: "16px",
                    lineHeight: 1.8,
                    fontFamily: "inherit",
                    whiteSpace: "pre-wrap",
                    overflowY: "auto",
                    "& ::selection": {
                      backgroundColor: "rgba(99, 102, 241, 0.3)",
                      color: "inherit",
                    },
                    "& span::selection": {
                      backgroundColor: "rgba(99, 102, 241, 0.3)",
                      color: "inherit",
                    },
                  }}
                >
                  {result?.sentence_analysis?.map((segment, index) => {
                    const isAI = segment.ai_score >= THRESHOLD_AI;
                    const isRefined =
                      segment.ai_score >= THRESHOLD_REFINED &&
                      segment.ai_score < THRESHOLD_AI;

                    return (
                      <Tooltip
                        key={index}
                        title={`AI Score: ${segment.ai_score}%`}
                        arrow
                        placement="top"
                      >
                        <span
                          onClick={() => setSelectedSentenceIndex(index)}
                          style={{
                            backgroundColor:
                              selectedSentenceIndex === index
                                ? "rgba(59, 130, 246, 0.3)" // Selection
                                : isAI
                                  ? "rgba(249, 115, 22, 0.4)" // Orange > 50
                                  : isRefined
                                    ? "rgba(59, 130, 246, 0.25)" // Blue 20-50
                                    : "transparent",
                            color: isAI ? "#ffffff" : undefined,
                            padding: "2px 0",
                            marginRight: "2px",
                            borderRadius: "4px",
                            transition:
                              "background-color 0.2s, box-shadow 0.2s, border-color 0.2s",
                            cursor: "pointer",
                            borderBottom:
                              selectedSentenceIndex === index
                                ? "1px solid #3b82f6"
                                : "1px solid transparent",
                            boxShadow:
                              selectedSentenceIndex === index
                                ? "0 0 0 2px rgba(59, 130, 246, 0.1)"
                                : "none",
                          }}
                        >
                          {segment.text}{" "}
                        </span>
                      </Tooltip>
                    );
                  })}
                  {!result?.sentence_analysis && (
                    <Typography color="error">
                      Analysis data missing.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#4b5563" : "text.secondary",
                    textAlign: "center",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    Min. 50 words for better accuracy •{" "}
                  </Box>
                  {inputText.split(/\s+/).filter((w) => w).length} words
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleScan}
                disabled={loading || !inputText.trim()}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <VerifiedUserOutlined />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: { xs: 2.5, sm: 4 },
                  py: 1,
                  fontSize: { xs: "15px", sm: "14px" },
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                {viewMode === "edit" ? "Scan for AI" : "Scan Again"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          {/* Conditional Right Panel */}
          {result && selectedSentenceIndex !== null ? (
            <SentenceDetailPanel
              sentenceAnalysis={result.sentence_analysis[selectedSentenceIndex]}
              isLocked={selectedSentenceIndex > 0}
              onClose={() => setSelectedSentenceIndex(null)}
            />
          ) : result ? (
            <Stack spacing={3}>
              {/* Global Analysis Result */}
              <Paper
                sx={{
                  p: 4,
                  borderRadius: "24px",
                  bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: isDarkMode ? "#9ca3af" : "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    Analysis Result
                  </Typography>
                  <Chip
                    icon={getConfidenceIcon(result.confidence)}
                    label={`${result.confidence.toUpperCase()} CONFIDENCE`}
                    size="small"
                    sx={{
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                      color: isDarkMode ? "white" : "text.primary",
                      fontWeight: 600,
                      fontSize: "10px",
                    }}
                  />
                </Box>

                <Box>
                  <Box
                    sx={{ position: "relative", display: "inline-flex", mb: 3 }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={result.ai_probability}
                      size={200}
                      thickness={2.5}
                      sx={{ color: getScoreColor(result.ai_probability) }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 800,
                          color: isDarkMode ? "white" : "text.primary",
                          letterSpacing: "-1px",
                        }}
                      >
                        {result.ai_probability}%
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#4b5563" : "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        AI LIKELIHOOD
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}`,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDarkMode ? "white" : "text.primary",
                        fontWeight: 500,
                      }}
                    >
                      {result.verdict}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#4b5563" : "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <InfoOutlined fontSize="inherit" />
                    {result.details.word_count} words,{" "}
                    {result.details.sentence_count} sentences analyzed
                  </Typography>

                  {/* Breakdown Legend */}
                  <Box
                    sx={{
                      mt: 4,
                      pt: 3,
                      borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: "#f97316",
                              boxShadow: "0 0 8px rgba(249, 115, 22, 0.4)",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? "#e5e7eb" : "text.secondary",
                              fontSize: "13px",
                            }}
                          >
                            AI-generated
                          </Typography>
                          <InfoOutlined
                            sx={{ fontSize: 14, color: "#6b7280" }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? "white" : "text.primary",
                            fontWeight: 600,
                          }}
                        >
                          {breakdown.ai}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: "#3b82f6",
                              boxShadow: "0 0 8px rgba(59, 130, 246, 0.4)",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? "#e5e7eb" : "text.secondary",
                              fontSize: "13px",
                            }}
                          >
                            Human-written & AI-refined
                          </Typography>
                          <InfoOutlined
                            sx={{ fontSize: 14, color: "#6b7280" }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? "white" : "text.primary",
                            fontWeight: 600,
                          }}
                        >
                          {breakdown.refined}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              border: "2px solid #4b5563",
                              bgcolor: "transparent",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? "#e5e7eb" : "text.secondary",
                              fontSize: "13px",
                            }}
                          >
                            Human-written
                          </Typography>
                          <InfoOutlined
                            sx={{ fontSize: 14, color: "#6b7280" }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? "white" : "text.primary",
                            fontWeight: 600,
                          }}
                        >
                          {breakdown.human}%
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Feedback Section */}
                  <Box
                    sx={{
                      mt: 4,
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "#f8fafc",
                      border: "1px dashed",
                      borderColor: isDarkMode
                        ? "rgba(255,255,255,0.1)"
                        : "#e2e8f0",
                      textAlign: "center",
                    }}
                  >
                    {feedbackStatus === "success" ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "#10b981", fontWeight: 600 }}
                      >
                        ✓ Thank you! This objective data will help us improve.
                      </Typography>
                    ) : pendingLabel ? (
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            mb: 1,
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          What was the actual AI percentage for this text?
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={3}
                          alignItems="center"
                          sx={{ px: 2, mb: 3 }}
                        >
                          <Slider
                            value={actualProb}
                            onChange={(_, val) => {
                              const v = val as number;
                              setActualProb(v);
                              // Auto-select all if 100%
                              if (v === 100)
                                setAffectedSections(["Intro", "Body", "End"]);
                            }}
                            min={0}
                            max={100}
                            step={1}
                            sx={{
                              color:
                                pendingLabel === "AI" ? "#f97316" : "#10b981",
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ minWidth: 50, fontWeight: 700 }}
                          >
                            {actualProb}%
                          </Typography>
                        </Stack>

                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            mb: 1,
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          Which parts are{" "}
                          {pendingLabel === "AI"
                            ? "AI-generated"
                            : "Human-written"}
                          ?
                        </Typography>

                        <Box
                          sx={{
                            mb: 3,
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {["Intro", "Body", "End"].map((sec) => (
                            <Chip
                              key={sec}
                              label={sec}
                              variant={
                                affectedSections.includes(sec)
                                  ? "filled"
                                  : "outlined"
                              }
                              onClick={() => {
                                if (affectedSections.includes(sec)) {
                                  setAffectedSections((prev) =>
                                    prev.filter((s) => s !== sec),
                                  );
                                } else {
                                  setAffectedSections((prev) => [...prev, sec]);
                                }
                              }}
                              sx={{
                                borderRadius: "8px",
                                px: 1.5,
                                fontWeight: 600,
                                fontSize: "12px",
                                bgcolor: affectedSections.includes(sec)
                                  ? pendingLabel === "AI"
                                    ? "#fde6d7"
                                    : "#dcfce7"
                                  : "transparent",
                                color: affectedSections.includes(sec)
                                  ? pendingLabel === "AI"
                                    ? "#ea580c"
                                    : "#16a34a"
                                  : "text.secondary",
                                borderColor: affectedSections.includes(sec)
                                  ? pendingLabel === "AI"
                                    ? "#f97316"
                                    : "#10b981"
                                  : "divider",
                                "&:hover": {
                                  bgcolor: affectedSections.includes(sec)
                                    ? pendingLabel === "AI"
                                      ? "#fde6d7"
                                      : "#dcfce7"
                                    : "rgba(0,0,0,0.04)",
                                },
                              }}
                            />
                          ))}
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setPendingLabel(null)}
                            sx={{ color: "text.secondary", fontSize: "11px" }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleReportError}
                            sx={{
                              bgcolor:
                                pendingLabel === "AI" ? "#f97316" : "#10b981",
                              fontSize: "11px",
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor:
                                  pendingLabel === "AI" ? "#ea580c" : "#059669",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Submit Report
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            mb: 1.5,
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          Is this result incorrect? Help us improve:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setPendingLabel("Human");
                              setActualProb(0);
                            }}
                            sx={{
                              fontSize: "11px",
                              py: 0.5,
                              borderColor: "#10b981",
                              color: "#10b981",
                              "&:hover": {
                                borderColor: "#059669",
                                bgcolor: "rgba(16, 185, 129, 0.05)",
                              },
                            }}
                          >
                            Actually Human
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setPendingLabel("AI");
                              setActualProb(100);
                              setAffectedSections(["Intro", "Body", "End"]);
                            }}
                            sx={{
                              fontSize: "11px",
                              py: 0.5,
                              borderColor: "#f97316",
                              color: "#f97316",
                              "&:hover": {
                                borderColor: "#ea580c",
                                bgcolor: "rgba(249, 115, 22, 0.05)",
                              },
                            }}
                          >
                            Actually AI
                          </Button>
                        </Stack>
                      </>
                    )}
                  </Box>
                </Box>
              </Paper>

              {/* Detailed Metrics */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "24px",
                  bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isDarkMode ? "white" : "text.primary",
                    mb: 3,
                    fontWeight: 600,
                  }}
                >
                  Detection Metrics
                </Typography>
                <Stack spacing={2}>
                  <MetricBar
                    label="Perplexity"
                    value={result.details.perplexity_score}
                    interpretation={result.interpretation.perplexity}
                    tooltip="Measures the predictability of the text (RoBERTa Model). Since AI models tend to choose the most probable words, they usually result in lower perplexity. Human writers often use surprising or varied vocabulary (Higher Perplexity)."
                  />
                  <MetricBar
                    label="Burstiness"
                    value={result.details.burstiness_score}
                    interpretation={result.interpretation.burstiness}
                    invertColor={true}
                    tooltip="Analyzes variations in sentence length and structure. AI models often generate sentences with uniform length and flow (Low Burstiness), whereas humans naturally vary their sentence structures and lengths (High Burstiness)."
                  />
                  <MetricBar
                    label="Lexical Diversity"
                    value={result.details.lexical_diversity}
                    interpretation={result.interpretation.diversity}
                    invertColor={true}
                    tooltip="Calculates the richness and variety of the vocabulary used. AI often stays within a safe, common, and academic vocabulary range (Low Diversity). Humans tend to use more spontaneous, synonymous, and diverse word choices."
                  />
                  <MetricBar
                    label="Transition Patterns"
                    value={result.details.transition_patterns}
                    tooltip="Scans for the signature bridging words often overused by LLMs like ChatGPT or Claude. Excessive use of conjunctive adverbs such as 'Moreover', 'In conclusion', and 'Additionally' is often flagged as an AI marker."
                  />
                  <MetricBar
                    label="Structure Uniformity"
                    value={result.details.sentence_structure}
                    tooltip="Measures the consistency of grammatical patterns across sentences. AI rarely exhibits human-like errors or shifts in punctuation and syntax, resulting in a perfectly uniform - but robotic - architecture (High Uniformity)."
                  />
                  <MetricBar
                    label="Repetition"
                    value={result.details.repetition_score}
                    tooltip="Detects recurring phrases or redundant arguments that are common when AI reaches its token constraints or hallucinations (Looping). High repetition often indicates a generated origin with limited creative range."
                  />
                </Stack>
              </Paper>

              {/* Recent Scans */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "24px",
                  bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isDarkMode ? "white" : "text.primary",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <History fontSize="small" sx={{ color: "#3b82f6" }} /> Recent
                  Scans
                </Typography>

                {recentScans.length === 0 ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#4b5563",
                      fontStyle: "italic",
                      display: "block",
                      textAlign: "center",
                      py: 2,
                    }}
                  >
                    No recent scans in this session.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {recentScans.map((scan, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{ overflow: "hidden", mr: 2, maxWidth: "70%" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#9ca3af",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {scan.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#4b5563" }}
                          >
                            {scan.date.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: scan.score > 50 ? "#ef4444" : "#10b981",
                            minWidth: "45px",
                            textAlign: "right",
                          }}
                        >
                          {scan.score}% AI
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          ) : (
            <Box sx={{ py: 6, opacity: 0.3, textAlign: "center" }}>
              <AssessmentOutlined
                sx={{
                  fontSize: 64,
                  mb: 2,
                  color: isDarkMode ? "white" : "text.secondary",
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? "white" : "text.secondary" }}
              >
                Scan text to see the AI likelihood score
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function SentenceDetailPanel({
  sentenceAnalysis,
  isLocked,
  onClose,
}: {
  sentenceAnalysis: SentenceAnalysis;
  isLocked: boolean;
  onClose: () => void;
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  if (isLocked) {
    return (
      <Paper
        sx={{
          p: 4,
          borderRadius: "24px",
          bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
          border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
          height: "100%",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", top: 16, right: 16 }}
        >
          Close
        </Button>

        {/* Blurry Background Content */}
        <Box
          sx={{
            filter: "blur(8px)",
            opacity: 0.3,
            width: "100%",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: isDarkMode ? "white" : "text.primary", mb: 2 }}
          >
            Main AI Contributors
          </Typography>
          <Box
            sx={{
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              height: 20,
              mb: 1,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              height: 20,
              mb: 3,
              width: "80%",
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              height: 100,
              borderRadius: 2,
            }}
          />
        </Box>

        {/* Lock Overlay */}
        <Box sx={{ position: "absolute", zIndex: 10, textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "rgba(239, 68, 68, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              mb: 2,
            }}
          >
            <VerifiedUserOutlined sx={{ fontSize: 32, color: "#ef4444" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: isDarkMode ? "white" : "text.primary",
              fontWeight: 700,
              mb: 1,
            }}
          >
            Advanced Analysis Locked
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#9ca3af", mb: 3, maxWidth: 300 }}
          >
            Deep breakdown of sentence structure, contributors, and detailed
            indicators are available on the Pro plan.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: "12px", fontWeight: 600 }}
          >
            Upgrade for Full Access
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "24px",
        bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
        position: "relative",
      }}
    >
      <Button
        size="small"
        onClick={onClose}
        sx={{ position: "absolute", top: 16, right: 16 }}
      >
        Close
      </Button>
      <Typography
        variant="h6"
        sx={{
          color: isDarkMode ? "white" : "text.primary",
          mb: 3,
          fontWeight: 600,
        }}
      >
        Sentence Analysis
      </Typography>

      <Box
        sx={{
          mb: 4,
          p: 2,
          bgcolor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          borderRadius: "12px",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: isDarkMode ? "#d1d5db" : "text.primary",
            fontStyle: "italic",
          }}
        >
          "
          {sentenceAnalysis.text.length > 100
            ? sentenceAnalysis.text.substring(0, 100) + "..."
            : sentenceAnalysis.text}
          "
        </Typography>
      </Box>

      <Typography
        variant="subtitle2"
        sx={{ color: isDarkMode ? "#9ca3af" : "text.secondary", mb: 2 }}
      >
        Main AI Contributors{" "}
        <InfoOutlined sx={{ fontSize: 14, verticalAlign: "middle", ml: 0.5 }} />
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Chip
            label={
              sentenceAnalysis.ai_score >= 50
                ? "AI-Generated"
                : sentenceAnalysis.ai_score >= 20
                  ? "Refined / Mixed"
                  : "Human-Written"
            }
            size="small"
            sx={{
              bgcolor:
                sentenceAnalysis.ai_score >= 50
                  ? "rgba(239,68,68,0.2)"
                  : sentenceAnalysis.ai_score >= 20
                    ? "rgba(59, 130, 246, 0.2)"
                    : "rgba(16,185,129,0.2)",
              color:
                sentenceAnalysis.ai_score >= 50
                  ? "#ef4444"
                  : sentenceAnalysis.ai_score >= 20
                    ? "#3b82f6"
                    : "#10b981",
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            mb: 1,
            display: "block",
          }}
        >
          Probability Level
        </Typography>
        <LinearProgress
          variant="determinate"
          value={sentenceAnalysis.ai_score}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
            "& .MuiLinearProgress-bar": {
              bgcolor:
                sentenceAnalysis.ai_score >= 50
                  ? "#ef4444"
                  : sentenceAnalysis.ai_score >= 20
                    ? "#3b82f6"
                    : "#10b981",
              borderRadius: 4,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? "white" : "text.primary",
            mt: 0.5,
            display: "block",
            textAlign: "right",
          }}
        >
          {sentenceAnalysis.ai_score}% Match
        </Typography>
      </Box>

      <Typography
        variant="subtitle2"
        sx={{ color: isDarkMode ? "#9ca3af" : "text.secondary", mb: 2 }}
      >
        Main AI-content Indicators
      </Typography>

      <Stack spacing={1}>
        {sentenceAnalysis.indicators?.map((indicator, i) => (
          <Chip
            key={i}
            label={indicator}
            sx={{
              justifyContent: "flex-start",
              bgcolor: "rgba(250, 204, 21, 0.1)",
              color: "#facc15",
              border: "1px solid rgba(250, 204, 21, 0.2)",
            }}
          />
        ))}
      </Stack>
    </Paper>
  );
}

function MetricBar({
  label,
  value,
  interpretation,
  tooltip,
  invertColor = false,
}: {
  label: string;
  value: number;
  interpretation?: string;
  tooltip?: string;
  invertColor?: boolean;
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const getColor = (val: number) => {
    // If invertColor is true, high values are GOOD (Green), low are BAD (Red)
    // Examples: Burstiness, Diversity
    if (invertColor) {
      if (val > 66) return "#10b981"; // Green (Good/Human)
      if (val > 33) return "#f59e0b"; // Orange
      return "#ef4444"; // Red (Bad/Uniform)
    }
    // Standard: High values are BAD (Red), low are GOOD (Green)
    // Examples: Perplexity (AI Score), Transitions, Repetition
    if (val < 33) return "#10b981"; // Green (Good/Human)
    if (val < 66) return "#f59e0b"; // Orange
    return "#ef4444"; // Red (Bad/AI)
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Tooltip title={tooltip || ""} arrow>
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? "#9ca3af" : "text.secondary",
              fontWeight: 500,
              cursor: "help",
            }}
          >
            {label}{" "}
            {tooltip && (
              <InfoOutlined
                sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
              />
            )}
          </Typography>
        </Tooltip>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {interpretation && (
            <Chip
              label={interpretation}
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                fontWeight: 600,
                bgcolor: isDarkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.04)",
                color: getColor(value),
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? "white" : "text.primary",
              fontWeight: 600,
            }}
          >
            {value.toFixed(1)}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
          "& .MuiLinearProgress-bar": {
            bgcolor: getColor(value),
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
}
